import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { createStructuredSelector } from "reselect";
import { database } from "../../firebase";
import logger from "../../utils/logger";

const initialState = {
  byOrganization: {},
  loadingByOrganization: {},
};

export const fetchTrackingPixelsByOrganization = createAsyncThunk(
  "trackingPixels/fetchTrackingPixelsByOrganization",
  async ({ organizationId }) => {
    try {
      const q = query(
        collection(database, "trackingPixels"),
        where("organizationId", "==", organizationId),
      );
      const snapshot = await getDocs(q);
      const pixels = snapshot.docs.map((docRef) => ({
        id: docRef.id,
        ...docRef.data(),
      }));
      return { organizationId, pixels };
    } catch (e) {
      logger.error("error fetching tracking pixels", e, { organizationId });
      return { organizationId, pixels: [] };
    }
  },
);

export const createTrackingPixel = createAsyncThunk(
  "trackingPixels/createTrackingPixel",
  async ({ organizationId, values }, { dispatch }) => {
    try {
      await addDoc(collection(database, "trackingPixels"), {
        organizationId,
        type: values.type || "meta",
        pixelId: values.pixelId,
        name: values.name || "",
        pageView: true,
        orderConfirmation: true,
        useForEvents: [],
      });
      await dispatch(fetchTrackingPixelsByOrganization({ organizationId }));
      return { organizationId };
    } catch (e) {
      logger.error("error creating tracking pixel", e, { organizationId });
      return { organizationId };
    }
  },
);

export const updateTrackingPixel = createAsyncThunk(
  "trackingPixels/updateTrackingPixel",
  async ({ organizationId, pixelId, values }, { dispatch }) => {
    try {
      await updateDoc(doc(database, "trackingPixels", pixelId), {
        ...values,
      });
      await dispatch(fetchTrackingPixelsByOrganization({ organizationId }));
      return { organizationId };
    } catch (e) {
      logger.error("error updating tracking pixel", e, { organizationId, pixelId });
      return { organizationId };
    }
  },
);

export const deleteTrackingPixel = createAsyncThunk(
  "trackingPixels/deleteTrackingPixel",
  async ({ organizationId, pixelId }, { dispatch }) => {
    try {
      await deleteDoc(doc(database, "trackingPixels", pixelId));
      await dispatch(fetchTrackingPixelsByOrganization({ organizationId }));
      return { organizationId };
    } catch (e) {
      logger.error("error deleting tracking pixel", e, { organizationId, pixelId });
      return { organizationId };
    }
  },
);

export const assignTrackingPixelsToEvent = createAsyncThunk(
  "trackingPixels/assignTrackingPixelsToEvent",
  async ({ organizationId, eventId, pixelIds }, { getState, dispatch }) => {
    try {
      const existingPixels =
        getState().trackingPixels.byOrganization[organizationId] || [];
      const pixels =
        existingPixels.length > 0
          ? existingPixels
          : (
              await dispatch(
                fetchTrackingPixelsByOrganization({ organizationId }),
              ).unwrap()
            ).pixels;

      const selectedPixelIds = Array.isArray(pixelIds) ? pixelIds : [];
      const updates = [];
      for (const pixel of pixels) {
        const useForEvents = Array.isArray(pixel.useForEvents)
          ? pixel.useForEvents
          : [];
        const containsEvent = useForEvents.includes(eventId);
        const shouldContainEvent = selectedPixelIds.includes(pixel.id);
        if (containsEvent === shouldContainEvent) {
          continue;
        }
        const updatedUseForEvents = shouldContainEvent
          ? [...useForEvents.filter((id) => id !== eventId), eventId]
          : useForEvents.filter((id) => id !== eventId);
        updates.push(
          updateDoc(doc(database, "trackingPixels", pixel.id), {
            useForEvents: updatedUseForEvents,
          }),
        );
      }

      await Promise.all(updates);
      await dispatch(fetchTrackingPixelsByOrganization({ organizationId }));
      return { organizationId };
    } catch (e) {
      logger.error("error assigning tracking pixel", e, { organizationId, eventId });
      return { organizationId };
    }
  },
);

const byOrganizationSelector = (state) => state.trackingPixels.byOrganization;
const loadingByOrganizationSelector = (state) => state.trackingPixels.loadingByOrganization;

const byOrganizationWithLoadingSelector = createSelector(
  [byOrganizationSelector, loadingByOrganizationSelector],
  (byOrganization, loadingByOrganization) => {
    const allKeys = [...new Set([...Object.keys(byOrganization), ...Object.keys(loadingByOrganization)])];
    return allKeys.reduce((acc, organizationId) => {
      acc[organizationId] = {
        pixels: byOrganization[organizationId] || [],
        loading: Boolean(loadingByOrganization[organizationId]),
      };
      return acc;
    }, {});
  },
);

const trackingPixelsSlice = createSlice({
  name: "trackingPixels",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrackingPixelsByOrganization.pending, (state, { meta }) => {
        const { organizationId } = meta.arg;
        state.loadingByOrganization[organizationId] = true;
      })
      .addCase(fetchTrackingPixelsByOrganization.fulfilled, (state, { payload }) => {
        const { organizationId, pixels } = payload;
        state.byOrganization[organizationId] = pixels;
        state.loadingByOrganization[organizationId] = false;
      });
  },
});

export const trackingPixelsSliceSelector = createStructuredSelector({
  byOrganization: byOrganizationWithLoadingSelector,
});

export default trackingPixelsSlice.reducer;
